import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Stack,
  Button,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Favorite,
  FavoriteOutlined,
  School,
  Schedule,
  Language,
  Money,
  Compare,
  StarRate,
} from '@mui/icons-material';

const ProgramCard = ({
  program,
  isFavorite = false,
  isCompared = false,
  onFavoriteToggle,
  onCompareToggle,
  onViewDetails,
  onApplyNow,
  viewMode
}) => {
  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    onFavoriteToggle?.(program.id);
  };

  const handleCompareClick = (e) => {
    e.stopPropagation();
    onCompareToggle?.(program.id);
  };

  // 获取货币符号
  const getCurrencySymbol = (currency) => {
    const currencyMap = {
      'MYR': 'RM',
      'CNY': '¥',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'SGD': 'S$',
      'AUD': 'A$'
    };
    return currencyMap[currency] || currency || 'RM';
  };

  const formatTuition = (tuition, currency = 'MYR') => {
    const currencySymbol = getCurrencySymbol(currency);
    
    // 如果是对象类型，取international字段
    if (tuition && typeof tuition === 'object' && tuition.international !== undefined) {
      const fee = tuition.international;
      if (fee && typeof fee === 'number' && fee > 0) {
        return `${currencySymbol} ${fee.toLocaleString()}`;
      }
      return `${currencySymbol} N/A`;
    }
    // 如果是数字类型
    if (typeof tuition === 'number') {
      if (tuition > 0) {
        return `${currencySymbol} ${tuition.toLocaleString()}`;
      }
      return `${currencySymbol} N/A`;
    }
    // 如果是字符串类型
    if (typeof tuition === 'string' && tuition.trim() !== '') {
      return tuition;
    }
    // 其他情况
    return `${currencySymbol} N/A`;
  };

  const getLevelColor = (level) => {
    const colors = {
      '学士': 'success',
      '硕士': 'primary',
      '博士': 'secondary',
      '文凭': 'info'
    };
    return colors[level] || 'default';
  };

  // 检查是否有有效的入学时间数据
  const hasValidIntakeMonths = () => {
    if (!program.intakeMonths || !Array.isArray(program.intakeMonths) || program.intakeMonths.length === 0) {
      return false;
    }
    // 检查是否都是无效值（如 '-'、'待定'、空字符串等）
    const validMonths = program.intakeMonths.filter(month => 
      month && month.trim() !== '' && month !== '-' && month !== '待定'
    );
    return validMonths.length > 0;
  };

  // 检查是否有有效的学制数据
  const hasValidDuration = () => {
    const duration = program.duration;
    // 如果没有值或者是 '-'，则视为无效
    if (!duration || duration.trim() === '' || duration === '-') {
      return false;
    }
    return true;
  };

  // 检查是否有有效的授课语言数据
  const hasValidLanguage = () => {
    const language = program.language;
    // 如果没有值或者是 '-'，则视为无效
    if (!language || language.trim() === '' || language === '-') {
      return false;
    }
    return true;
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: viewMode === 'list' ? 'row' : 'column',
        transition: 'all 0.3s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
        position: 'relative',
        border: isCompared ? 2 : 0,
        borderColor: isCompared ? 'primary.main' : 'transparent',
      }}
      onClick={() => onViewDetails?.(program.id)}
    >
      {/* 推荐标签 */}
      {program.featured && (
        <Chip
          label="推荐"
          color="error"
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 1,
            fontWeight: 600,
          }}
        />
      )}

      {/* 收藏和对比按钮 */}
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <Stack direction="row" spacing={1}>
          <Tooltip title={isCompared ? "移除对比" : "添加对比"}>
            <IconButton
              size="small"
              onClick={handleCompareClick}
              sx={{
                backgroundColor: 'white',
                '&:hover': { backgroundColor: 'grey.100' },
                color: isCompared ? 'primary.main' : 'grey.600'
              }}
            >
              <Compare />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFavorite ? "取消收藏" : "收藏专业"}>
            <IconButton
              size="small"
              onClick={handleFavoriteClick}
              sx={{
                backgroundColor: 'white',
                '&:hover': { backgroundColor: 'grey.100' }
              }}
            >
              {isFavorite ? 
                <Favorite color="error" /> : 
                <FavoriteOutlined sx={{ color: 'grey.600' }} />
              }
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* 专业图片 */}
      <CardMedia
        component="div"
        sx={{
          height: viewMode === 'list' ? 120 : 0,
          width: viewMode === 'list' ? 200 : '100%',
          paddingTop: viewMode === 'list' ? 0 : '41.67%', // 600:250 的宽高比 (250/600 = 0.4167)
          backgroundImage: program.image ? 
            `url(${program.image})` : 
            'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          backgroundSize: 'contain', // 改为 contain 确保图片完全显示
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          minHeight: viewMode === 'list' ? 120 : 'auto',
          flexShrink: 0, // 防止图片区域被压缩
        }}
      >
        {!program.image && (
          <School sx={{ 
            fontSize: viewMode === 'list' ? 40 : 60, 
            color: 'white', 
            opacity: 0.8,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }} />
        )}
      </CardMedia>

      <CardContent sx={{ 
        flexGrow: 1, 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        {/* 内容区域 - 自动填充空间 */}
        <Box sx={{ flexGrow: 1 }}>
          {/* 大学信息 */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {program.university?.name || '未知院校'}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <StarRate sx={{ fontSize: 16, color: 'warning.main', mr: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              {program.university?.ranking || 999}
            </Typography>
          </Box>
        </Box>

        {/* 专业名称 */}
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, lineHeight: 1.3 }}>
          {program.name || '未知专业'}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {program.nameEn || program.name || '未知专业'}
        </Typography>

        {/* 专业标签 */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip 
            label={program.level || '学士'} 
            size="small" 
            color={getLevelColor(program.level || '学士')}
            variant="outlined"
          />
          {program.category && program.category !== '-' && (
            <Chip 
              label={program.category} 
              size="small" 
              variant="outlined"
            />
          )}
          {program.scholarship && (
            <Chip 
              label="奖学金" 
              size="small" 
              color="warning"
              variant="outlined"
            />
          )}
        </Stack>

        {/* 专业信息 */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          {hasValidDuration() && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Schedule sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                学制：{program.duration}
              </Typography>
            </Box>
          )}
          {hasValidLanguage() && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Language sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                授课语言：{program.language}
              </Typography>
            </Box>
          )}
          {(program.tuitionFee && typeof program.tuitionFee === 'number' && program.tuitionFee > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Money sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                学费：{formatTuition(program.tuitionFee, program.currency)} /年
              </Typography>
            </Box>
          )}
        </Stack>

        {/* 入学时间 */}
        {hasValidIntakeMonths() && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              入学时间：
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {program.intakeMonths.map((month, index) => (
                <Chip
                  key={index}
                  label={month}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Stack>
          </Box>
        )}
        </Box>

        {/* 分隔线和操作按钮 - 固定在底部 */}
        <Divider sx={{ my: 2, flexShrink: 0 }} />

        {/* 操作按钮 */}
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails?.(program.id);
            }}
          >
            查看详情
          </Button>
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              onApplyNow?.(program);
            }}
          >
            立即申请
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProgramCard;
